import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft, Upload, X, FileText, AlertTriangle, File, Image as ImageIcon,
  Save, Edit3, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import PermitMap from '../../components/PermitMap';
import EditRequestDiff from '../../components/EditRequestDiff';

const EditRequestCreate = () => {
  const { id: permitId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reworkFrom = searchParams.get('reworkFrom');
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [landRegistryDocument, setLandRegistryDocument] = useState(null);
  const [registryMapDocument, setRegistryMapDocument] = useState(null);
  const [files, setFiles] = useState([]);
  const [boundaryPoints, setBoundaryPoints] = useState([]);
  const [reason, setReason] = useState('');
  const [assignedUser, setAssignedUser] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: '',
      father_name: '',
      village: '',
      post_office: '',
      police_station: '',
      size_sqft: '',
      validity_date: '',
      north_boundary: '',
      south_boundary: '',
      east_boundary: '',
      west_boundary: '',
      location_description: '',
      remarks: ''
    }
  });

  const watchedFields = watch();

  // Fetch permit data
  const { data: permitData, isLoading: loadingPermit, error: permitError } = useQuery(
    ['permit', permitId],
    async () => {
      const response = await api.get(`/permits/${permitId}`);
      return response.data.permit;
    }
  );

  // Fetch reviewers (EAC users) for assignment
  const { data: reviewersData } = useQuery(
    ['reviewers', permitData?.subdivisionId],
    async () => {
      const response = await api.get(`/users/reviewers/list?subdivisionId=${permitData?.subdivisionId}`);
      return response.data.reviewers;
    },
    {
      enabled: !!permitData?.subdivisionId
    }
  );

  // Fetch rejected edit request when reworking
  const { data: reworkData } = useQuery(
    ['editRequest', reworkFrom],
    async () => {
      const response = await api.get(`/edit-requests/${reworkFrom}`);
      return response.data.editRequest;
    },
    { enabled: !!reworkFrom }
  );

  // Role hierarchy for filtering reviewers
  const ROLE_HIERARCHY = ['bo', 'eac', 'sdo', 'adc', 'dc'];

  // Filter to get users with higher roles in same subdivision + all DC users
  const availableReviewers = useMemo(() => {
    if (!reviewersData || !user) return [];

    const currentRoleIndex = ROLE_HIERARCHY.indexOf(user.role);

    // Filter users with higher roles in same subdivision + all DC users
    return reviewersData.filter(u => {
      const reviewerRoleIndex = ROLE_HIERARCHY.indexOf(u.role);

      // Must be a higher role
      if (reviewerRoleIndex <= currentRoleIndex) return false;

      // DC users always included (district-wide)
      if (u.role === 'dc') return true;

      // Other higher roles must be in same subdivision
      return u.subdivisionId === permitData?.subdivisionId;
    });
  }, [reviewersData, user, permitData?.subdivisionId]);

  // Pre-fill form with permit data
  useEffect(() => {
    if (permitData) {
      reset({
        name: permitData.name || '',
        father_name: permitData.father_name || '',
        village: permitData.village || '',
        post_office: permitData.post_office || '',
        police_station: permitData.police_station || '',
        size_sqft: permitData.size_sqft || '',
        validity_date: permitData.validity_date ? permitData.validity_date.split('T')[0] : '',
        north_boundary: permitData.north_boundary || '',
        south_boundary: permitData.south_boundary || '',
        east_boundary: permitData.east_boundary || '',
        west_boundary: permitData.west_boundary || '',
        location_description: permitData.location_description || '',
        remarks: permitData.remarks || ''
      });

      if (permitData.boundaryPoints && Array.isArray(permitData.boundaryPoints)) {
        setBoundaryPoints(permitData.boundaryPoints);
      }
    }
  }, [permitData, reset]);

  // When reworking a rejected request, overlay the previous requested changes onto the form
  useEffect(() => {
    if (!reworkData || !permitData) return;

    const changes = reworkData.requestedChanges || {};
    if (Object.keys(changes).length > 0) {
      reset(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(changes).filter(([k]) => k !== 'boundaryPoints')
        )
      }));
      if (changes.boundaryPoints && Array.isArray(changes.boundaryPoints)) {
        setBoundaryPoints(changes.boundaryPoints);
      }
    }

    if (reworkData.reason) {
      setReason(reworkData.reason);
    }
  }, [reworkData, permitData, reset]);

  // Calculate changes between original and current values
  const getRequestedChanges = () => {
    if (!permitData) return {};

    const changes = {};
    const fieldsToCompare = [
      'name', 'father_name', 'village', 'post_office', 'police_station',
      'size_sqft', 'validity_date', 'north_boundary', 'south_boundary',
      'east_boundary', 'west_boundary', 'location_description', 'remarks'
    ];

    fieldsToCompare.forEach(field => {
      const originalValue = permitData[field] || '';
      const newValue = watchedFields[field] || '';

      // Handle date comparison
      if (field === 'validity_date') {
        const originalDate = originalValue ? originalValue.split('T')[0] : '';
        if (originalDate !== newValue) {
          changes[field] = newValue;
        }
      } else if (originalValue !== newValue) {
        changes[field] = newValue;
      }
    });

    // Compare boundary points
    const originalPoints = JSON.stringify(permitData.boundaryPoints || []);
    const newPoints = JSON.stringify(boundaryPoints);
    if (originalPoints !== newPoints && boundaryPoints.length >= 3) {
      changes.boundaryPoints = boundaryPoints;
    }

    return changes;
  };

  const getDocumentChanges = () => {
    const docChanges = {};

    if (landRegistryDocument) {
      docChanges.landRegistryDocument = {
        originalName: landRegistryDocument.name,
        size: landRegistryDocument.size,
        mimetype: landRegistryDocument.type
      };
    }

    if (registryMapDocument) {
      docChanges.registryMapDocument = {
        originalName: registryMapDocument.name,
        size: registryMapDocument.size,
        mimetype: registryMapDocument.type
      };
    }

    if (files.length > 0) {
      docChanges.scannedDocuments = files.map(f => ({
        originalName: f.name,
        size: f.size,
        mimetype: f.type
      }));
    }

    return Object.keys(docChanges).length > 0 ? docChanges : null;
  };

  const requestedChanges = getRequestedChanges();
  const documentChanges = getDocumentChanges();
  const hasChanges = Object.keys(requestedChanges).length > 0 || documentChanges;

  // Create edit request mutation
  const createEditRequest = useMutation(
    async () => {
      const formData = new FormData();
      formData.append('reason', reason);
      formData.append('requestedChanges', JSON.stringify(requestedChanges));
      formData.append('assignedUser', assignedUser);

      if (landRegistryDocument) {
        formData.append('landRegistryDocument', landRegistryDocument);
      }
      if (registryMapDocument) {
        formData.append('registryMapDocument', registryMapDocument);
      }
      files.forEach(file => {
        formData.append('documents', file);
      });

      const response = await api.post(`/edit-requests/${permitId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['permit', permitId]);
        queryClient.invalidateQueries(['pendingEdit', permitId]);
        toast.success('Edit request submitted successfully!');
        navigate(`/permits/${permitId}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create edit request');
      }
    }
  );

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleLandRegistryChange = (e) => {
    const file = e.target.files[0];
    if (file) setLandRegistryDocument(file);
  };

  const handleRegistryMapChange = (e) => {
    const file = e.target.files[0];
    if (file) setRegistryMapDocument(file);
  };

  const onSubmit = () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for this edit request');
      return;
    }
    if (!assignedUser) {
      toast.error('Please select a reviewer');
      return;
    }
    if (!hasChanges) {
      toast.error('No changes detected. Please make at least one change.');
      return;
    }
    createEditRequest.mutate();
  };

  const getFileIcon = (file) => {
    if (file.type?.includes('pdf')) {
      return <FileText className="h-8 w-8 text-red-500" />;
    }
    if (file.type?.includes('image')) {
      return <ImageIcon className="h-8 w-8 text-blue-500" />;
    }
    return <File className="h-8 w-8 text-gray-500" />;
  };

  if (loadingPermit) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (permitError) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading permit data. Please try again.
        </div>
      </div>
    );
  }

  // Check if permit is Active or Renewal_Needed
  if (!['Active', 'Renewal_Needed'].includes(permitData?.status)) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-700">
            <AlertTriangle className="h-5 w-5" />
            <span>Edit requests can only be created for Active or Renewal Required permits.</span>
          </div>
          <Link to={`/permits/${permitId}`} className="mt-3 inline-block text-primary-600 hover:underline">
            Back to Permit
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to={`/permits/${permitId}`} className="text-gray-600 hover:text-gray-800">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Edit3 className="h-6 w-6" />
              Request Edit
            </h1>
            <p className="text-gray-500">
              Permit #{permitData?.permit_no} - {permitData?.name}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-blue-700 text-sm">
          <strong>Edit Request Workflow:</strong> Your changes will be reviewed by EAC &rarr; SDO &rarr; ADC/DC
          before being applied to the permit. All changes are tracked in the audit trail.
        </p>
      </div>

      {/* ReWork Banner */}
      {reworkFrom && reworkData && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <p className="text-orange-800 text-sm font-semibold mb-1">Reworking a Rejected Request</p>
          <p className="text-orange-700 text-sm">
            Your previous changes have been pre-filled below. Please address the rejection reason and re-submit.
          </p>
          {reworkData.rejectionReason && (
            <p className="text-orange-700 text-sm mt-2">
              <strong>Rejection reason:</strong> "{reworkData.rejectionReason}"
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Edit Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Fields</h2>

          <form className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Permit Holder Name
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Father's Name
                </label>
                <input
                  type="text"
                  {...register('father_name')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Village/Ward
                </label>
                <input
                  type="text"
                  {...register('village')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Post Office
                </label>
                <input
                  type="text"
                  {...register('post_office')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Police Station
                </label>
                <input
                  type="text"
                  {...register('police_station')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Size (sq ft)
                </label>
                <input
                  type="text"
                  {...register('size_sqft')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validity Date
              </label>
              <input
                type="date"
                {...register('validity_date')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Boundaries */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Land Boundaries</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">North</label>
                  <input
                    type="text"
                    {...register('north_boundary')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">South</label>
                  <input
                    type="text"
                    {...register('south_boundary')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">East</label>
                  <input
                    type="text"
                    {...register('east_boundary')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">West</label>
                  <input
                    type="text"
                    {...register('west_boundary')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Location Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Description
              </label>
              <textarea
                {...register('location_description')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <textarea
                {...register('remarks')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Map for Boundary Points */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Boundary Points (Map)</h3>
              <PermitMap
                boundaryPoints={boundaryPoints}
                setBoundaryPoints={setBoundaryPoints}
                isEditable={true}
              />
            </div>

            {/* Document Uploads */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Document Changes</h3>

              {/* Land Registry Document */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Replace Land Registry Document
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleLandRegistryChange}
                    className="text-sm"
                  />
                  {landRegistryDocument && (
                    <button
                      type="button"
                      onClick={() => setLandRegistryDocument(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {landRegistryDocument && (
                  <p className="text-xs text-green-600 mt-1">
                    New file: {landRegistryDocument.name}
                  </p>
                )}
              </div>

              {/* Registry Map Document */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Replace Registry Map Document
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleRegistryMapChange}
                    className="text-sm"
                  />
                  {registryMapDocument && (
                    <button
                      type="button"
                      onClick={() => setRegistryMapDocument(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {registryMapDocument && (
                  <p className="text-xs text-green-600 mt-1">
                    New file: {registryMapDocument.name}
                  </p>
                )}
              </div>

              {/* Supporting Documents */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add Supporting Documents
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleFileChange}
                  className="text-sm"
                />
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          {getFileIcon(file)}
                          <span className="text-sm text-gray-700">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Preview & Submit Panel */}
        <div className="space-y-6">
          {/* Changes Preview */}
          {showPreview && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Changes Preview</h2>
              {hasChanges ? (
                <EditRequestDiff
                  originalValues={permitData}
                  requestedChanges={requestedChanges}
                  documentChanges={documentChanges}
                />
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No changes detected yet. Make changes to the fields above.
                </p>
              )}
            </div>
          )}

          {/* Reason & Assignment */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Submit Request</h2>

            {/* Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Edit <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Explain why these changes are needed..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Reviewer Assignment */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assign to Reviewer <span className="text-red-500">*</span>
              </label>
              <select
                value={assignedUser}
                onChange={(e) => setAssignedUser(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Reviewer</option>
                {availableReviewers.map(reviewer => (
                  <option key={reviewer.id} value={reviewer.id}>
                    {reviewer.fullName} ({reviewer.role.toUpperCase()} - {reviewer.designation || reviewer.role.toUpperCase()})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select a reviewer with a higher role to process your edit request.
              </p>
            </div>

            {/* Changes Summary */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Changes Summary</h4>
              <p className="text-sm text-gray-600">
                {Object.keys(requestedChanges).length} field change(s)
                {documentChanges && (
                  <>
                    {documentChanges.landRegistryDocument && ', Land Registry replacement'}
                    {documentChanges.registryMapDocument && ', Registry Map replacement'}
                    {documentChanges.scannedDocuments?.length > 0 && `, ${documentChanges.scannedDocuments.length} new document(s)`}
                  </>
                )}
                {!hasChanges && ' - No changes detected'}
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Link
                to={`/permits/${permitId}`}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-center"
              >
                Cancel
              </Link>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!hasChanges || !reason || !assignedUser || createEditRequest.isLoading}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {createEditRequest.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Submit Edit Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRequestCreate;
